import { useState } from 'react'
import Layout, { Card } from '@/components/Layout'
import { BarChart, Heatmap } from '@/components/Charts'
import type { Stats } from '@/types'

interface LeaderRow { name: string; total: number; streak: number; is_current: boolean }
interface Props {
  uuid: string
  stats: Stats
  daily: Record<string, number>
  leaderboard: LeaderRow[]
  badges_earned: number
  badges_total: number
  has_data: boolean
}

function Stat({ value, label, color = '' }: { value: React.ReactNode; label: string; color?: string }) {
  return (
    <div className="bg-white border border-[#e7ebf2] rounded-2xl px-4 py-3.5">
      <div className={'text-[26px] font-extrabold leading-tight ' + color}>{value}</div>
      <div className="text-[#707d90] text-[13px] mt-1">{label}</div>
    </div>
  )
}

export default function Index({ uuid, stats, daily, leaderboard, badges_earned, badges_total, has_data }: Props) {
  const medals = ['🥇', '🥈', '🥉']
  const [copied, setCopied] = useState(false)
  const share = async () => {
    const url = `${window.location.origin}/u/${uuid}`
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }
  return (
    <Layout>
      <Card title="📊 我的看板">
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={share}
            className="bg-white border border-[#e7ebf2] rounded-xl text-sm px-3 py-1.5 font-medium"
          >
            {copied ? '✅ 链接已复制' : '🔗 分享打卡页'}
          </button>
        </div>
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
          <Stat value={stats.balance} label="💰 可用积分" color="text-[#5b7cfa]" />
          <Stat value={stats.total} label="📈 累计赚得" />
          <Stat value={<>{stats.streak} {stats.streak > 0 ? '🔥' : ''}</>} label="连续打卡（天）" color="text-[#f5a623]" />
          <Stat value={stats.earns} label="✅ 完成次数" color="text-[#2ec16b]" />
          <Stat value={stats.goals_done} label="🏆 达成目标" color="text-[#ff6b9d]" />
          <Stat value={`${badges_earned}/${badges_total}`} label="🏅 成就" />
        </div>
        {!has_data && <div className="text-[#707d90] text-center py-6 text-sm">这位人员还没有数据，去「🎯 目标」创建第一个目标吧。</div>}
      </Card>

      <Card title="📈 近 30 天积分趋势"><BarChart daily={daily} /></Card>

      <Card title="🟩 打卡热力图（近 13 周）">
        <div style={{ overflowX: 'auto' }}><Heatmap daily={daily} /></div>
      </Card>

      <Card title="🏁 排行榜">
        {leaderboard.map((r, i) => (
          <div key={i} className="flex items-center gap-2.5 py-2 border-b border-[#e7ebf2] last:border-0">
            <span className="w-6 h-6 rounded-full grid place-items-center font-extrabold text-[13px] bg-[#eef2ff] text-[#5b7cfa]">{medals[i] || i + 1}</span>
            <span className={'flex-1 ' + (r.is_current ? 'font-bold' : '')}>{r.name}{r.is_current ? ' （你）' : ''}</span>
            <span className="text-[#707d90] text-[13px]">🔥 {r.streak} 天</span>
            <span className="font-bold text-[#5b7cfa] whitespace-nowrap">累计 {r.total} 分</span>
          </div>
        ))}
      </Card>
    </Layout>
  )
}
